import { useState, useEffect, useMemo, useCallback, startTransition } from 'react';
import { isDemoBrand } from '../utils/constants';
import { formatDateForAPI, addDays } from '../utils/formatters';
import { fetchWithBrand } from '../utils/api';

// Helper: format Date to YYYY-MM-DD (local time)
const formatYMD = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * The most complex hook. Fetches:
 * - ga-traffic-source (both filtered and all)
 * - ga-device-performance
 * - ga-platform-daily/monthly
 *
 * Contains useMemos: gaDailyTotalsMap, trendPercentages, comparisonAggregates, trafficSourcesAnalytics.
 */
export function useGA4(brandId, dateRange, startDate, endDate, getComparisonWindows) {
  const [gaTrafficSourceData, setGaTrafficSourceData] = useState(null);
  const [gaTrafficSourceAllData, setGaTrafficSourceAllData] = useState(null);
  const [gaDevicePerf, setGaDevicePerf] = useState([]);
  const [gaPlatformDaily, setGaPlatformDaily] = useState([]);
  const [gaPlatformMonthly, setGaPlatformMonthly] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // --- Mount-only fetches: all-time traffic source + platform monthly ---
  useEffect(() => {
    if (!brandId) return;

    const fetchAllGaTrafficData = async () => {
      if (isDemoBrand(brandId)) {
        const now = new Date();
        const sources = [
          { source: 'google', medium: 'cpc' },
          { source: 'google', medium: 'organic' },
          { source: 'meta', medium: 'paid_social' },
          { source: 'instagram', medium: 'paid_social' },
          { source: 'direct', medium: '(none)' },
          { source: 'email', medium: 'newsletter' },
          { source: 'bing', medium: 'cpc' },
          { source: 'referral', medium: 'affiliate' },
        ];
        const detailedData = [];
        for (let i = 0; i < 30; i++) {
          const d = new Date(now);
          d.setDate(d.getDate() - (29 - i));
          const dateStr = d.toISOString().split('T')[0];
          sources.forEach((src, sIdx) => {
            const baseFactor = [1.5, 0.8, 1.2, 0.6, 0.4, 0.3, 0.25, 0.2][sIdx];
            detailedData.push({
              date: dateStr,
              source: src.source,
              medium: src.medium,
              sessions: Math.round((2500 * baseFactor) * (0.85 + Math.random() * 0.3)),
              eventCount: Math.round((150 * baseFactor) * (0.85 + Math.random() * 0.3)),
              totalRevenue: Math.round((98000 * baseFactor) * (0.85 + Math.random() * 0.3)),
            });
          });
        }
        setGaTrafficSourceAllData({ detailedData });
        return;
      }
      try {
        const response = await fetchWithBrand('/api/google-ads/ga-traffic-source?dateRange=all', brandId);
        if (response.ok) {
          const data = await response.json();
          setGaTrafficSourceAllData(data);
        }
      } catch {
        // silent
      }
    };

    const fetchGaPlatformMonthly = async () => {
      if (isDemoBrand(brandId)) {
        const now = new Date();
        const demoRows = [];
        const platforms = ['mobile', 'desktop', 'tablet'];
        for (let i = 0; i < 6; i++) {
          const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 5 + i, 1));
          const monthKey = d.toISOString().split('T')[0];
          platforms.forEach((platform, pIdx) => {
            const baseFactor = pIdx === 0 ? 1.4 : pIdx === 1 ? 1 : 0.3;
            demoRows.push({
              month: monthKey,
              platform,
              sessions: Math.round((25000 + i * 2000) * baseFactor),
              users: Math.round((18000 + i * 1500) * baseFactor),
              transactions: Math.round((700 + i * 50) * baseFactor),
              revenue: Math.round((620000 + i * 40000) * baseFactor),
            });
          });
        }
        setGaPlatformMonthly(demoRows);
        return;
      }
      try {
        const resp = await fetchWithBrand('/api/google-ads/ga-platform-monthly', brandId);
        if (resp.ok) {
          const data = await resp.json();
          setGaPlatformMonthly(Array.isArray(data?.rows) ? data.rows : []);
        }
      } catch {
        setGaPlatformMonthly([]);
      }
    };

    fetchAllGaTrafficData();
    fetchGaPlatformMonthly();
  }, [brandId]);

  // --- Date-range-dependent fetches ---

  // GA Traffic Source (date range)
  useEffect(() => {
    if (dateRange === 'custom' && (!startDate || !endDate)) return;
    if (!brandId) return;

    const fetchGATrafficSourceData = async () => {
      if (isDemoBrand(brandId)) {
        setIsLoading(true);
        const demo = {
          detailedData: [
            { date: '2025-10-01', source: 'google', medium: 'cpc', sessions: 2000, eventCount: 120, totalRevenue: 90000 },
            { date: '2025-10-01', source: 'meta', medium: 'paid_social', sessions: 800, eventCount: 55, totalRevenue: 38000 },
            { date: '2025-10-02', source: 'google', medium: 'organic', sessions: 700, eventCount: 18, totalRevenue: 12000 },
          ],
          totals: { users: 2800, sessions: 3500, eventCount: 193, totalRevenue: 140000 },
        };
        setGaTrafficSourceData(demo);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        let apiUrl = `/api/google-ads/ga-traffic-source?dateRange=${dateRange}`;
        if (dateRange === 'custom' && startDate && endDate) {
          const startStr = formatDateForAPI(startDate);
          const endStr = formatDateForAPI(endDate);
          apiUrl = `/api/google-ads/ga-traffic-source?dateRange=custom&startDate=${startStr}&endDate=${endStr}`;
        }
        const response = await fetchWithBrand(apiUrl, brandId);
        if (!response.ok) throw new Error(`API error: ${response.status}`);
        const data = await response.json();
        startTransition(() => {
          setGaTrafficSourceData(data);
        });
      } catch {
        // silent
      } finally {
        setIsLoading(false);
      }
    };

    fetchGATrafficSourceData();
  }, [brandId, dateRange, startDate, endDate]);

  // GA Device Performance (date range)
  useEffect(() => {
    if (dateRange === 'custom' && (!startDate || !endDate)) return;
    if (!brandId) return;

    const fetchGADevicePerf = async () => {
      if (isDemoBrand(brandId)) {
        const demoDevices = [
          { key: 'mobile', deviceCategory: 'mobile', sessions: 52000, users: 38500, transactions: 1250, revenue: 1180000, bounceRate: 45.2, avgSessionDuration: 185 },
          { key: 'desktop', deviceCategory: 'desktop', sessions: 28000, users: 22000, transactions: 980, revenue: 1450000, bounceRate: 35.8, avgSessionDuration: 320 },
          { key: 'tablet', deviceCategory: 'tablet', sessions: 5200, users: 4100, transactions: 145, revenue: 165000, bounceRate: 42.5, avgSessionDuration: 245 },
        ];
        setGaDevicePerf(demoDevices);
        return;
      }

      try {
        let apiUrl = `/api/google-ads/ga-device-performance?groupBy=device&dateRange=${dateRange}`;
        if (dateRange === 'custom' && startDate && endDate) {
          const startStr = formatDateForAPI(startDate);
          const endStr = formatDateForAPI(endDate);
          apiUrl = `/api/google-ads/ga-device-performance?groupBy=device&dateRange=custom&startDate=${startStr}&endDate=${endStr}`;
        }
        const res = await fetchWithBrand(apiUrl, brandId);
        if (!res.ok) throw new Error(`GA Device Perf API error: ${res.status}`);
        const json = await res.json();
        const rows = Array.isArray(json?.rows) ? json.rows : [];
        // Normalize device order for display
        const order = ['mobile', 'desktop', 'tablet'];
        const byKey = {};
        rows.forEach(r => {
          const key = (r.key || r.deviceCategory || '').toString().toLowerCase();
          byKey[key] = r;
        });
        const ordered = order.filter(k => byKey[k]).map(k => byKey[k]);
        setGaDevicePerf(ordered.length ? ordered : rows);
      } catch {
        setGaDevicePerf([]);
      }
    };

    fetchGADevicePerf();
  }, [brandId, dateRange, startDate, endDate]);

  // GA Platform Daily (date range)
  const fetchGaPlatformDaily = useCallback(async () => {
    if (!brandId) return;
    if (isDemoBrand(brandId)) {
      const now = new Date();
      const demoRows = [];
      const platforms = ['mobile', 'desktop', 'tablet'];
      const numDays = dateRange === 'today' ? 1 : dateRange === 'yesterday' ? 1 : dateRange === 'last7' ? 7 : dateRange === 'last30' ? 30 : 30;
      for (let i = 0; i < numDays; i++) {
        const d = new Date(now);
        d.setDate(d.getDate() - (numDays - 1 - i));
        const dateStr = d.toISOString().split('T')[0];
        const dayOfWeek = d.getDay();
        const weekendFactor = (dayOfWeek === 0 || dayOfWeek === 6) ? 1.12 : 1;
        platforms.forEach((platform, pIdx) => {
          const baseFactor = pIdx === 0 ? 1.5 : pIdx === 1 ? 1 : 0.25;
          demoRows.push({
            date: dateStr,
            platform,
            sessions: Math.round((2800 * baseFactor * weekendFactor) * (0.9 + Math.random() * 0.2)),
            users: Math.round((2100 * baseFactor * weekendFactor) * (0.9 + Math.random() * 0.2)),
            transactions: Math.round((85 * baseFactor * weekendFactor) * (0.9 + Math.random() * 0.2)),
            revenue: Math.round((72000 * baseFactor * weekendFactor) * (0.9 + Math.random() * 0.2)),
          });
        });
      }
      setGaPlatformDaily(demoRows);
      return;
    }

    try {
      let url = '/api/google-ads/ga-platform-daily';
      if (dateRange === 'custom' && startDate && endDate) {
        const s = formatDateForAPI(startDate);
        const e = formatDateForAPI(endDate);
        url += `?startDate=${s}&endDate=${e}`;
      } else {
        const today = new Date();
        const toYMD = (d) => formatYMD(d);
        let start = null;
        let end = toYMD(today);
        if (dateRange === 'today') {
          start = end;
        } else if (dateRange === 'yesterday') {
          const y = addDays(today, -1);
          start = toYMD(y);
          end = toYMD(y);
        } else if (dateRange === 'last7') {
          const s7 = addDays(today, -6);
          start = toYMD(s7);
        } else if (dateRange === 'last30') {
          const s30 = addDays(today, -29);
          start = toYMD(s30);
        } else if (dateRange === 'last90') {
          const s90 = addDays(today, -89);
          start = toYMD(s90);
        }
        if (start) {
          url += `?startDate=${start}&endDate=${end}`;
        }
      }
      const resp = await fetchWithBrand(url, brandId);
      if (resp.ok) {
        const data = await resp.json();
        setGaPlatformDaily(Array.isArray(data?.rows) ? data.rows : []);
      }
    } catch {
      setGaPlatformDaily([]);
    }
  }, [brandId, dateRange, startDate, endDate]);

  // Fetch platform daily on mount and on range change
  useEffect(() => {
    if (dateRange === 'custom' && (!startDate || !endDate)) return;
    fetchGaPlatformDaily();
  }, [fetchGaPlatformDaily, dateRange, startDate, endDate]);

  // --- Internal helper: aggregate GA4 metrics within a date window ---
  const aggregateGA4ForWindow = useCallback((startStr, endStr) => {
    const source = gaTrafficSourceAllData?.detailedData?.length
      ? gaTrafficSourceAllData.detailedData
      : (gaTrafficSourceData?.detailedData || []);
    if (!source.length) return { users: 0, sessions: 0, eventCount: 0, totalRevenue: 0 };
    const within = (d) => d >= startStr && d <= endStr;
    return source.reduce((acc, item) => {
      const d = typeof item.date === 'string' ? (item.date.includes('T') ? item.date.split('T')[0] : item.date) : formatYMD(new Date(item.date));
      if (!within(d)) return acc;
      acc.users += Number(item.users) || 0;
      acc.sessions += Number(item.sessions) || 0;
      acc.eventCount += Number(item.eventCount) || 0;
      acc.totalRevenue += Number(item.totalRevenue) || 0;
      return acc;
    }, { users: 0, sessions: 0, eventCount: 0, totalRevenue: 0 });
  }, [gaTrafficSourceAllData, gaTrafficSourceData]);

  // --- useMemo: gaDailyTotalsMap ---
  const gaDailyTotalsMap = useMemo(() => {
    if (!gaTrafficSourceData?.detailedData) return null;
    const map = new Map();
    for (const item of gaTrafficSourceData.detailedData) {
      const d = typeof item.date === 'string' ? (item.date.includes('T') ? item.date.split('T')[0] : item.date) : formatYMD(new Date(item.date));
      if (!map.has(d)) {
        map.set(d, { revenue: 0, sessions: 0, transactions: 0 });
      }
      const agg = map.get(d);
      agg.revenue += Number(item.totalRevenue) || 0;
      agg.sessions += Number(item.sessions) || 0;
      agg.transactions += Number(item.eventCount) || 0;
    }
    return map;
  }, [gaTrafficSourceData]);

  // --- useMemo: trendPercentages ---
  const trendPercentages = useMemo(() => {
    const windows = getComparisonWindows();
    if (!windows) return { users: 0, sessions: 0, eventCount: 0, totalRevenue: 0, conversionRate: 0 };
    const curAgg = aggregateGA4ForWindow(windows.current.start, windows.current.end);
    const prevAgg = aggregateGA4ForWindow(windows.previous.start, windows.previous.end);
    const pct = (cur, prev) => (prev === 0 ? 0 : ((cur - prev) / prev) * 100);
    const curRate = curAgg.sessions > 0 ? (curAgg.eventCount / curAgg.sessions) * 100 : 0;
    const prevRate = prevAgg.sessions > 0 ? (prevAgg.eventCount / prevAgg.sessions) * 100 : 0;
    return {
      users: pct(curAgg.users, prevAgg.users),
      sessions: pct(curAgg.sessions, prevAgg.sessions),
      eventCount: pct(curAgg.eventCount, prevAgg.eventCount),
      totalRevenue: pct(curAgg.totalRevenue, prevAgg.totalRevenue),
      conversionRate: pct(curRate, prevRate),
    };
  }, [gaTrafficSourceAllData, gaTrafficSourceData, getComparisonWindows, aggregateGA4ForWindow]);

  // --- useMemo: comparisonAggregates ---
  const comparisonAggregates = useMemo(() => {
    const windows = getComparisonWindows();
    if (!windows) return null;
    const cur = aggregateGA4ForWindow(windows.current.start, windows.current.end);
    const prev = aggregateGA4ForWindow(windows.previous.start, windows.previous.end);
    const curCR = cur.sessions > 0 ? (cur.eventCount / cur.sessions) * 100 : 0;
    const prevCR = prev.sessions > 0 ? (prev.eventCount / prev.sessions) * 100 : 0;
    return { cur, prev, curCR, prevCR };
  }, [gaTrafficSourceAllData, gaTrafficSourceData, getComparisonWindows, aggregateGA4ForWindow]);

  // --- useMemo: trafficSourcesAnalytics ---
  const trafficSourcesAnalytics = useMemo(() => {
    if (!gaTrafficSourceData?.detailedData || gaTrafficSourceData.detailedData.length === 0) {
      return { tableData: [], totals: null };
    }

    const sourceGroups = {};
    for (const item of gaTrafficSourceData.detailedData) {
      const source = item.source || 'Others';
      const medium = item.medium || 'none';
      const key = `${source} / ${medium}`;
      if (!sourceGroups[key]) {
        sourceGroups[key] = {
          source,
          medium,
          sourcemedium: key,
          type: medium === 'organic' ? 'organic' : medium === 'cpc' ? 'cpc' : medium === 'social' ? 'social' : medium === 'email' ? 'email' : medium === 'referral' ? 'referral' : 'direct',
          sessions: 0,
          transactions: 0,
          revenue: 0,
          users: 0,
          iconBg: medium === 'organic' ? 'bg-blue-100' : medium === 'cpc' ? 'bg-red-100' : medium === 'social' ? 'bg-indigo-100' : medium === 'email' ? 'bg-amber-100' : medium === 'referral' ? 'bg-pink-100' : 'bg-green-100',
          iconColor: medium === 'organic' ? 'text-blue-600' : medium === 'cpc' ? 'text-red-600' : medium === 'social' ? 'text-indigo-600' : medium === 'email' ? 'text-amber-600' : medium === 'referral' ? 'text-pink-600' : 'text-green-600',
        };
      }
      sourceGroups[key].sessions += Number(item.sessions) || 0;
      sourceGroups[key].transactions += Number(item.eventCount) || 0;
      sourceGroups[key].revenue += Number(item.totalRevenue) || 0;
      sourceGroups[key].users += Number(item.users) || 0;
    }

    const allSourceMediums = Object.values(sourceGroups).sort((a, b) => b.sessions - a.sessions);
    const top20 = allSourceMediums.slice(0, 20);
    const othersData = allSourceMediums.slice(20);

    if (othersData.length > 0) {
      const othersGroup = {
        source: 'Others',
        medium: 'mixed',
        sourcemedium: 'Others / mixed',
        type: 'others',
        sessions: othersData.reduce((sum, item) => sum + item.sessions, 0),
        transactions: othersData.reduce((sum, item) => sum + item.transactions, 0),
        revenue: othersData.reduce((sum, item) => sum + item.revenue, 0),
        users: othersData.reduce((sum, item) => sum + item.users, 0),
        iconBg: 'bg-gray-100',
        iconColor: 'text-gray-600',
      };
      top20.push(othersGroup);
    }

    const processedData = top20.map(group => ({
      source: group.source,
      medium: group.medium,
      sourcemedium: group.sourcemedium,
      type: group.type,
      sessions: group.sessions,
      transactions: group.transactions,
      revenue: group.revenue,
      conversionRate: group.sessions > 0 ? (group.transactions / group.sessions * 100) : 0,
      iconBg: group.iconBg,
      iconColor: group.iconColor,
    }));

    const totals = processedData.reduce((acc, item) => {
      acc.sessions += item.sessions;
      acc.transactions += item.transactions;
      acc.revenue += item.revenue;
      return acc;
    }, { sessions: 0, transactions: 0, revenue: 0 });
    const totalsWithCr = { ...totals, conversionRate: totals.sessions > 0 ? (totals.transactions / totals.sessions * 100) : 0 };

    return { tableData: processedData, totals: totalsWithCr };
  }, [gaTrafficSourceData]);

  // --- generateGA4ChartData: builds Chart.js-ready data ---
  const generateGA4ChartData = useCallback((metric) => {
    if (!gaTrafficSourceData?.detailedData) {
      return { labels: [], current: [], previous: [] };
    }

    const windows = getComparisonWindows();
    if (!windows) return { labels: [], current: [], previous: [] };

    const enumerateDates = (startStr, endStr) => {
      const dates = [];
      const d = new Date(startStr);
      const end = new Date(endStr);
      while (d <= end) {
        dates.push(formatYMD(d));
        d.setDate(d.getDate() + 1);
      }
      return dates;
    };

    const sumMetricForDate = (source, dateStr, metricKey) => {
      if (!Array.isArray(source)) return 0;
      if (metricKey === 'sessionCount') {
        let sessions = 0;
        let transactions = 0;
        source.forEach(item => {
          const d = typeof item.date === 'string' ? (item.date.includes('T') ? item.date.split('T')[0] : item.date) : formatYMD(new Date(item.date));
          if (d === dateStr) {
            sessions += Number(item.sessions) || 0;
            transactions += Number(item.eventCount) || 0;
          }
        });
        return sessions > 0 ? (transactions / sessions) * 100 : 0;
      }

      let sum = 0;
      source.forEach(item => {
        const d = typeof item.date === 'string' ? (item.date.includes('T') ? item.date.split('T')[0] : item.date) : formatYMD(new Date(item.date));
        if (d !== dateStr) return;
        switch (metricKey) {
          case 'totalSales':
            sum += Number(item.totalRevenue) || 0;
            break;
          case 'saleCount':
            sum += Number(item.sessions) || 0;
            break;
          case 'conversionRate':
            sum += Number(item.users) || 0;
            break;
          case 'refundAmount':
            sum += Number(item.eventCount) || 0;
            break;
          default:
            sum += Number(item.totalRevenue) || 0;
        }
      });
      return sum;
    };

    const currentDates = enumerateDates(windows.current.start, windows.current.end);
    const prevDates = enumerateDates(windows.previous.start, windows.previous.end);
    const labels = currentDates.map((s, idx) => {
      const cur = new Date(s).toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
      const prev = prevDates[idx] ? new Date(prevDates[idx]).toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) : '';
      return `${cur} | ${prev}`;
    });

    const currentSource = gaTrafficSourceData.detailedData;
    const previousSource = gaTrafficSourceAllData?.detailedData?.length ? gaTrafficSourceAllData.detailedData : gaTrafficSourceData.detailedData;

    const current = currentDates.map(d => sumMetricForDate(currentSource, d, metric));
    const previous = prevDates.map(d => sumMetricForDate(previousSource, d, metric));

    return { labels, current, previous };
  }, [gaTrafficSourceData, gaTrafficSourceAllData, getComparisonWindows]);

  return {
    gaTrafficSourceData,
    gaTrafficSourceAllData,
    gaDevicePerf,
    gaPlatformDaily,
    gaPlatformMonthly,
    gaDailyTotalsMap,
    trendPercentages,
    comparisonAggregates,
    trafficSourcesAnalytics,
    isLoading,
    generateGA4ChartData,
  };
}
